// Bottom-left/right viewport overlays: speed indicator, grid/axes/frame
// toggles, and a help legend. Plus a small toast that hooks into status
// messages without obstructing the canvas.

import { renderIcon, iconButton } from './icons';
import { Grid3x3, Axis3d, Focus, Gauge, Keyboard } from 'lucide';
import type { EditorTool } from './EditorTool';
import type { FlyControls } from './FlyControls';

export interface OverlayCallbacks {
  onFrameSelection(): void;
}

export class ViewportOverlay {
  readonly bl: HTMLElement;
  readonly tr: HTMLElement;
  readonly toast: HTMLElement;
  private tool: EditorTool;
  private fly: FlyControls;
  private speedEl: HTMLElement;
  private gridBtn: HTMLButtonElement;
  private axesBtn: HTMLButtonElement;
  private unsub: () => void;
  private toastTimer = 0;
  private speedTick = 0;

  constructor(parent: HTMLElement, tool: EditorTool, fly: FlyControls, cbs: OverlayCallbacks) {
    this.tool = tool;
    this.fly = fly;
    this.bl = document.createElement('div');
    this.bl.className = 'ed-overlay ed-overlay-bl';
    parent.appendChild(this.bl);

    this.speedEl = document.createElement('div');
    this.speedEl.className = 'ed-overlay-row';
    this.speedEl.innerHTML = `<span class="ed-icon">${renderIcon(Gauge, 14)}</span><span>Speed: <strong id="ed-speed">8.0</strong> m/s</span>`;
    this.bl.appendChild(this.speedEl);

    this.gridBtn = iconButton({ icon: Grid3x3, title: 'Grid (G)', onClick: () => this.tool.setShowGrid(!this.tool.get().showGrid) });
    this.axesBtn = iconButton({ icon: Axis3d, title: 'Axes (A)', onClick: () => this.tool.setShowAxes(!this.tool.get().showAxes) });
    const frameBtn = iconButton({ icon: Focus, title: 'Frame selection (F)', onClick: cbs.onFrameSelection });
    this.bl.appendChild(this.gridBtn);
    this.bl.appendChild(this.axesBtn);
    this.bl.appendChild(frameBtn);

    this.tr = document.createElement('div');
    this.tr.className = 'ed-overlay ed-overlay-tr';
    this.tr.innerHTML = `<span class="ed-icon">${renderIcon(Keyboard, 14)}</span>
      <span class="ed-overlay-help">
        <strong>T</strong> control (fly) · <strong>Q</strong> select · <strong>W</strong> move · <strong>E</strong> rotate · <strong>R</strong> scale · <strong>F</strong> frame · <strong>Esc</strong> release
      </span>`;
    parent.appendChild(this.tr);

    this.toast = document.createElement('div');
    this.toast.className = 'ed-toast';
    parent.appendChild(this.toast);

    this.unsub = this.tool.subscribe(() => this.apply());
    this.apply();
  }

  flash(message: string, isError = false): void {
    this.toast.textContent = message;
    this.toast.classList.toggle('is-error', isError);
    this.toast.classList.add('show');
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toast.classList.remove('show'), 2400);
  }

  /** Call once per frame to update the speed indicator without forcing a
   *  re-render of the whole overlay every tick. */
  step(): void {
    this.speedTick = (this.speedTick + 1) % 6;
    if (this.speedTick !== 0) return;
    const el = this.speedEl.querySelector('#ed-speed');
    if (el) el.textContent = (8 * this.fly.speedScale).toFixed(1);
  }

  dispose(): void {
    this.unsub();
    this.bl.remove();
    this.tr.remove();
    this.toast.remove();
  }

  private apply(): void {
    const t = this.tool.get();
    this.gridBtn.classList.toggle('is-active', t.showGrid);
    this.axesBtn.classList.toggle('is-active', t.showAxes);
  }
}
